<template lang="pug">
div(v-if="canComputeFeeDetails")
  div(v-if="mode === 'buyService'")
    .fee-summary-card(:class="{ 'is-expanded': showFeeDetails }")
      .fee-breakdown-content(v-if="showFeeDetails")
        h3
          span.title {{ $t('service_cate') }}
          span {{ $t(selectedCate) }}
        h3
          span.title {{ $t('service_plan') }}
          span(v-if="selectedTime") {{ $t(`plan_names.${selectedPlan}`) }}
          span(v-else) {{ $t(addonPlanNameKey) }}
        TooltipProvider(:delayDuration="Number(100)" v-if="purchaseType.type === 'upgrade'")
          Tooltip
            TooltipTrigger(as-child)
              h3
                span.title.flex.items-center.gap-1
                  | {{ $t('pricing.upgrade_time') }}
                  Icon(name="mdi:help-circle" class="w-4 h-4")
                span {{ $t('pricing.month', { n: currentService?.extra.buy_length || 0 }) }}
            TooltipContent
              span {{ $t('pricing.upgrade_all_time') }}
        h3(v-if="purchaseType.type === 'upgrade'")
          span.title {{ $t('pricing.upgrade_billing_ratio') }}
          span {{ formatUpgradeProrationRatio(details.upgradeProrationRatio) }}
        h3(v-if="!['upgrade', 'downgrade'].includes(purchaseType.type) && selectedTime")
          span.title {{ $t('pricing.selected_time') }}
          Popover
            PopoverTrigger
              span {{ $t('pricing.month', { n: selectedTime }) }}
                span(v-if="selectedTime === 1 && purchaseType.type === 'new_buy'") ({{ purchasePeriodStr }})
            PopoverContent.border-0.bg-none.w-40.p-1(side="top" align="end" data-side="top" data-align="end")
              .flex.items-center.justify-evenly.gap-1
                Icon(name="material-symbols-light:remove" class="clickable w-12" @click="() => localSelectedTime -= 1")
                Input.w-20(type="number" step="1" min="1" v-model="localSelectedTime")
                Icon(name="material-symbols-light:add" class="clickable w-12" @click="() => localSelectedTime += 1")
        h3(v-if="selectedTime")
          span.title {{ $t('pricing.due_after_payment') }}
          span {{ details.dueTimeAfterPurchase }}
        h3(v-if="!selectedTime && !allPre")
          span.title {{ $t('pricing.extra_amount') }}
          span {{ extraPremium }} GB
        h3(v-if="!selectedTime")
          span.title {{ $t('pricing.extra_amount_price') }}
          span {{ addonPriceText }}
        div(v-if="purchaseType.type === 'new_buy'")
          Separator.my-4
          h3
            span.title {{ $t('pricing.full_month_fee') }}
            span {{ feeList.currency }}{{ details.selectedPlanFee?.toFixed(2) }}
          TooltipProvider(:delayDuration="Number(100)" v-if="selectedTime > 1")
            Tooltip
              TooltipTrigger(as-child)
                h3
                  span.title.flex.items-center.gap-1
                    | {{ $t('pricing.billed_months') }}
                    Icon(name="mdi:help-circle" class="w-4 h-4")
                  span {{ $t('pricing.month', { n: details.realTime }) }}
              TooltipContent
                span {{ $t('pricing.billed_months_hint') }}
        div(v-if="promoCode && !['upgrade', 'downgrade'].includes(purchaseType.type) && selectedTime")
          Separator.my-4
          //- PLink(type="primary" @click.native="showPromo = true") Have a Promo code
          h3.promo
            span.title {{ $t('promo.title') }}
            span.promo-info(v-if="promoCode && promoCodeInfoStr") {{ promoCodeInfoStr }}
            span
              input(type="text" readonly :placeholder="$t('promo.hint')" v-model="promoCodeInput" debounce="300")
            p
              PTag(v-if="promoError" type="warning") {{ promoError }}

        h3(v-if="details.vatRate > 0")
          span.title {{ $t('pricing.vat_rate') }}
          span {{ details.vatRate * 100 }}%
        h3(v-if="details.roundUplocalFee > 0")
          span.title {{ $t('pricing.local_calc_fee') }}
          span {{ totalCostDisplay }}
        div(v-if="details.balance > 0 && details.roundUplocalFee > 0")
          h3
            span.title {{ $t('account_balance') }}
            span {{ balanceDisplay }}
        h3.balance-covered-row(v-if="balanceCoversTotal")
          Icon.balance-covered-icon(name="material-symbols:check-circle-rounded")
          span {{ $t('pricing.balance_covers_total') }}
        Separator.my-3
      button.fee-summary-toggle(type="button" @click="toggleFeeDetails" :class="{ 'is-right-side-summary': isRightSideSummary }" :aria-expanded="String(showFeeDetails)" :aria-label="$t('pricing.fee-details')")
        span.fee-summary-title {{ $t('billing.due') }}
        span.fee-summary-amount
          span {{ details.amountDueDisplay }}
          small(v-if="details.forcedMinimalFee") {{ $t('billing.due_rounded') }}
        Icon.fee-summary-chevron(v-if="!isRightSideSummary" name="material-symbols:keyboard-arrow-down-rounded")
  div(v-else)
    h3
      span.title {{ $t('pricing.add_credit') }}
      span {{ feeList.currency }}{{ topUpAmount?.toFixed(2) }}
    Separator.my-4
    h3.large
      span.title {{ $t('billing.due') }}
      span {{ details.amountDueDisplay }}
        h5.subtitle(v-if="details.forcedMinimalFee")
          small {{ $t('billing.due_rounded') }}
  h3.subtitle.secondary(v-if="details.willPayWithRMB")
    small {{ $t('pricing.currency_converted_to_cny') }}
  h3.subtitle.secondary.payment-note.is-first(v-if="details.canPayWithRMB && $i18n.locale === 'zh'")
    small {{ $t('pricing.can_pay_with_yin_lian') }}
  h3.subtitle.secondary.payment-note(v-if="details.isYunshanfu")
    small {{ $t('pricing.yunshanfu_usd') }}
  h3.subtitle.secondary.payment-note.special-warning(v-if="details.isYunshanfu")
    small {{ $t('billing.do_not_scan_qr.a') }}
  slot

  //- pre.text-align-left {{ details }}
</template>

<script lang="ts" setup>
import { Separator } from '@/components/ui/separator'
import { formatDataAddonQuotePrice } from '@/utils/dataAddonPricing'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
const pricing = usePricing()
const dayjs = useDayjs()
const SUMMARY_RIGHT_SIDE_MEDIA_QUERY = '(min-width: 1181px)'
const showFeeDetails = ref(false)
const isRightSideSummary = ref(false)
const userToggledFeeDetails = ref(false)
const promoCodeInfo = ref<number | null>(null)
const promoError = ref<string | null>(null)
let stopSummaryDetailsDefaultListener: (() => void) | null = null

const $t = useI18n().t
const $config = useRuntimeConfig().public
const service = useUserService()

const mode = computed(() => pricing.mode)
const selectedCate = computed(() => pricing.cate)
const selectedPlan = computed(() => pricing.plan ?? (pricing.cate === 'home' ? 6 : 4))
const selectedTime = computed(() => pricing.time)
const extraPremium = computed(() => pricing.extraPremium)
const allPre = computed(() => pricing.allPre)
const purchaseType = computed(() => pricing.purchaseType)
const selectedPaymentMethod = computed(() => pricing.selectedPaymentMethod ?? {})
const topUpAmount = computed(() => pricing.topUpAmount)
const promoCode = computed({
  get: () => pricing.promoCode,
  set: (val) => pricing.setState({ promoCode: String(val || '') }),
})
const currentService = computed(() => (service as any)[selectedCate.value as string] || {})
const feeList = computed(() => $config.feeList)
const addonPlanNameKey = computed(() => {
  if (pricing.currentDataAddonProduct === 'allplatform_quote') return 'pricing.standard_data_addon'
  return selectedCate.value === 'home' ? 'pricing.extra_data' : 'service_details.extra_premium'
})
const addonPriceText = computed(() => {
  return formatDataAddonQuotePrice(pricing.dataAddonQuote, feeList.value.currency)
})

const localSelectedTime = computed({
  get: () => pricing.time,
  set: (value) => {
    if (Number.isFinite(Number(value)) && Number(value) >= 0) {
      pricing.time = Number(value)
    }
  },
})

const promoCodeInput = computed({
  get: () => pricing.promoCode || '',
  set: (val) => {
    promoCode.value = String(val || '')
  },
})

const promoCodeInfoStr = computed(() => {
  if (promoCodeInfo.value) {
    if (promoCodeInfo.value === 1) return $t('billing.invalid_promo')
    if (useI18n().locale.value === 'zh') return `${promoCodeInfo.value * 10}折`
    return `-${100 - promoCodeInfo.value * 100}%`
  }
  return ''
})

const maxPreForCurrentMonth = computed(() => {
  return pricing.maxPreForCurrentMonth
})

const canComputeFeeDetails = computed(() => {
  const dataAddonMode = mode.value === 'buyService'
    && ['allplatform', 'home'].includes(selectedCate.value)
    && selectedTime.value === 0
  return !dataAddonMode || pricing.dataAddonQuote.ok
})

const details = computed(() =>
  computeFeeDetails({
    mode: mode.value as any,
    selectedCate: selectedCate.value,
    selectedTime: selectedTime.value,
    selectedPlan: selectedPlan.value,
    extraPremium: extraPremium.value,
    allPre: allPre.value,
    currentAllPre: currentService.value?.extra?.all_pre || 0,
    maxPreForCurrentMonth: maxPreForCurrentMonth.value,
    showPromo: Boolean(promoCode.value),
    promoCode: promoCode.value,
    purchaseType: purchaseType.value,
    selectedPaymentMethod: selectedPaymentMethod.value || undefined,
    topUpAmount: topUpAmount.value,
    currentService: currentService.value,
    promoCodeInfoRatio: promoCodeInfo.value ?? undefined,
    dataAddonQuote: pricing.dataAddonQuote,
  }),
)
const formatMoney = (amount: number) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: feeList.value.currencyCode || 'EUR',
    }).format(Number(amount) || 0)
  } catch {
    return `${feeList.value.currency}${(Number(amount) || 0).toFixed(2)}`
  }
}
const totalCostDisplay = computed(() => formatMoney(details.value.roundUplocalFee))
const balanceDisplay = computed(() => `-${formatMoney(details.value.balance)}`)
const balanceCoversTotal = computed(() => {
  return mode.value === 'buyService'
    && details.value.exactLocalFee > 0
    && details.value.amountDue <= 0
    && details.value.balance >= details.value.exactLocalFee
})
function formatPurchasePeriod(startDate: string, dueDate: string) {
  const match = /^\d{4}\/(\d{2})\/(\d{2})$/.exec(dueDate)
  if (!match) {
    throw new Error(`Invalid purchase due date: ${dueDate}`)
  }
  return `${startDate} → ${Number(match[1])}/${Number(match[2])}`
}

function formatUpgradeProrationRatio(ratio: number | null) {
  if (ratio == null || !Number.isFinite(ratio) || ratio < 0 || ratio > 1) {
    throw new Error(`Invalid upgrade proration ratio: ${String(ratio)}`)
  }
  return `${Number((ratio * 100).toFixed(2))}%`
}

const purchasePeriodStr = computed(() => {
  const today = dayjs().tz('Asia/Shanghai')
  return formatPurchasePeriod(today.format('M/D'), details.value.dueTimeAfterPurchase)
})

function applySummaryDetailsDefault(nextIsRightSideSummary: boolean) {
  isRightSideSummary.value = nextIsRightSideSummary
  if (userToggledFeeDetails.value) {
    return
  }
  showFeeDetails.value = nextIsRightSideSummary
}

function toggleFeeDetails() {
  userToggledFeeDetails.value = true
  showFeeDetails.value = !showFeeDetails.value
}

onMounted(() => {
  const summaryPlacement = window.matchMedia(SUMMARY_RIGHT_SIDE_MEDIA_QUERY)
  applySummaryDetailsDefault(summaryPlacement.matches)
  const handleSummaryPlacementChange = (event: MediaQueryListEvent) => {
    applySummaryDetailsDefault(event.matches)
  }
  summaryPlacement.addEventListener('change', handleSummaryPlacementChange)
  stopSummaryDetailsDefaultListener = () => {
    summaryPlacement.removeEventListener('change', handleSummaryPlacementChange)
  }

  if (pricing.promoCode) {
    checkPromoCode()
  }
})

onUnmounted(() => {
  stopSummaryDetailsDefaultListener?.()
})

const checkPromoCode = useDebounceFn(async function () {
  try {
    const data = await usePay.checkPromoCode({ promoCode: promoCode.value })
    promoCodeInfo.value = data as any
    promoError.value = null
  } catch (err) {
    promoError.value = (err as any).data?.message
    promoCodeInfo.value = null
  }
}, 500)

watch(
  () => pricing.promoCode,
  async (val) => {
    if (val) await checkPromoCode()
    else {
      promoError.value = null
      promoCodeInfo.value = null
    }
  },
)
</script>

<style lang="postcss" scoped>
.fee-summary-card {
  overflow: hidden;
  border: 1px solid var(--pricing-border);
  border-radius: 1rem;
  background: var(--pricing-solid-surface);
  box-shadow: none;
}

.fee-breakdown-content {
  display: grid;
  gap: 0;
  padding: 0.9rem 0.95rem 0;
}

.fee-summary-toggle {
  display: grid;
  align-items: center;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.65rem;
  width: 100%;
  min-height: 3.6rem;
  padding: 0.8rem 0.95rem;
  border: 0;
  background: transparent;
  color: var(--pricing-text);
  cursor: pointer;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
}

.fee-summary-toggle.is-right-side-summary {
  grid-template-columns: minmax(0, 1fr) auto;
}

.fee-summary-toggle:hover,
.fee-summary-toggle:active,
.fee-summary-toggle:focus-visible {
  background: transparent;
}

.fee-summary-title {
  min-width: 0;
  color: var(--pricing-muted);
  font-size: 0.9375rem;
  font-weight: 500;
}

.fee-summary-amount {
  display: grid;
  justify-items: end;
  min-width: 0;
  font-size: clamp(1.35rem, 5vw, 2rem);
  font-weight: 500;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  white-space: nowrap;
}

.fee-summary-amount small {
  margin-top: 0.25rem;
  color: var(--pricing-muted);
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0;
}

.fee-summary-chevron {
  flex: none;
  color: var(--pricing-muted);
  font-size: 1.45rem;
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
}

.fee-summary-card.is-expanded .fee-summary-chevron {
  transform: rotate(180deg);
}

.light .fee-summary-card {
  background: var(--pricing-white);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.light .fee-summary-toggle:hover,
.light .fee-summary-toggle:active,
.light .fee-summary-toggle:focus-visible {
  background: transparent;
}

.balance-covered-row {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end;
  gap: 0.35rem;
  margin-top: -0.2rem;
  color: var(--pricing-accent) !important;
  font-size: 0.75rem !important;
  font-weight: 500;
  line-height: 1 !important;
}

.balance-covered-row span {
  display: inline-flex !important;
  align-items: center;
  color: inherit !important;
  font-size: inherit !important;
  font-weight: inherit !important;
  line-height: 1 !important;
}

.balance-covered-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  flex: 0 0 1em;
  width: 1em;
  height: 1em;
  color: currentColor !important;
  font-size: 1rem;
  line-height: 1;
  transform: translateY(-0.06em);
}

@media (max-width: 768px) {
  .fee-breakdown-content {
    padding: 0.68rem 0.75rem 0;
  }

  .fee-summary-toggle {
    min-height: 2.9rem;
    gap: 0.45rem;
    padding: 0.52rem 0.72rem;
  }

  .fee-summary-title {
    font-size: 0.82rem;
  }

  .fee-summary-amount {
    font-size: clamp(1.18rem, 4.4vw, 1.55rem);
  }

  .fee-summary-chevron {
    font-size: 1.22rem;
  }
}

@media (max-height: 700px) {
  .fee-breakdown-content {
    padding: 0.65rem 0.75rem 0;
  }

  .fee-summary-toggle {
    min-height: 2.75rem;
    padding: 0.46rem 0.68rem;
  }
}

.summary {
  .details {
    .otp {
      margin-top: 10px;
      text-align: right;
      font-size: 1rem;
      color: var(--secondary-color);
    }

    .promo {
      input {
        font-size: 20px;
        text-align: center;
        border: none;
        border-bottom: 1px solid var(--secondary-color);
        border-radius: 0;
        background: transparent;
        color: var(--primary-color);

        /* width: 120px; */
        @media (max-width: 768px) {
          width: 120px;
        }
      }

      .promo-info {
        font-weight: 300;
        background: var(--alizarin);
        color: var(--pricing-white);
        margin-right: 10px;
        display: inline-block;
        border-radius: 15px;
        padding: 2px 5px;
        font-size: 15px;
      }
    }

    h5.subtitle {
      margin-top: 10px;
    }

    h3 {
      display: block;
      position: relative;
      text-align: right;
      font-size: 1.1rem;
      line-height: 1.3rem;
      color: var(--primary-color);
      white-space: pre-wrap;
      /* css-3 */
      word-wrap: break-word;
      min-height: 1.3rem;
      /* padding-left: 140px; */

      .subtitle {
        font-weight: 300;
        color: var(--secondary-color);
        font-size: 15px;
      }

      /* font-weight: 500; */
      @media (max-width: 768px) {
        /* font-size: 15px; */
        /* padding-left: 100px; */
      }

      margin-bottom: 10px;

      &.large {
        span {
          font-weight: 500 !important;
          color: var(--primary-color) !important;
        }

        span:last-of-type {
          font-size: 30px;
          /* background: var(--sun-flower); */
        }
      }

      span {
        display: inline-block;

        /* right: 0; */
        &.title {
          color: var(--secondary-color);
          position: absolute;
          left: 0;
          font-weight: 400;
          text-align: left;
          font-size: 1rem;

          i {
            margin-left: 5px;
          }
        }
      }
    }
  }
}

.special-warning {
  text-align: right !important;
  background: transparent;
  color: var(--pricing-danger) !important;
  padding: 0;
  border: 0;
  backdrop-filter: none;

  small {
    color: inherit !important;
  }

  .light & {
    color: var(--pricing-danger-strong) !important;
  }
}
</style>
